// Login page — matches the Zühlke serif/petrol palette of the dashboard shell.

import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

const BrandMark = () => (
    <div className="flex items-center gap-3" aria-hidden>
        <span className="relative inline-block w-8 h-8">
            <span className="absolute inset-0 rounded-full bg-[hsl(var(--accent))]/15" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[hsl(var(--accent))]" />
        </span>
        <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--mute))] font-medium">Zühlke</div>
            <div className="display text-base leading-none">Digital Assets Lodestar</div>
        </div>
    </div>
);

const Login = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const { status, login } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const redirectTo = location.state?.from || "/";

    if (status === "authed") return <Navigate to={redirectTo} replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        const { ok, error: err } = await login(username.trim().toLowerCase(), password);
        setSubmitting(false);
        if (ok) {
            navigate(redirectTo, { replace: true });
        } else {
            setError(err || "Invalid username or password");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center page-grain px-6">
            <div className="w-full max-w-md">
                <div className="mb-8 flex justify-center">
                    <BrandMark />
                </div>
                <section className="card-flat p-8 space-y-5" data-testid="login-card">
                    <header>
                        <div className="eyebrow">Restricted access</div>
                        <h1 className="display text-2xl mt-1.5 leading-snug">Sign in to continue</h1>
                        <p className="text-sm text-[hsl(var(--mute))] mt-2 leading-relaxed">
                            This dashboard is shared with banking clients under non-disclosure. Use the
                            credentials your Zühlke contact provided.
                        </p>
                    </header>

                    <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Username</span>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoFocus
                                autoComplete="username"
                                data-testid="login-username"
                                className="mt-1 w-full px-3 py-2.5 text-sm bg-[hsl(var(--surface-raised))] border border-[hsl(var(--line))] rounded outline-none focus:border-[hsl(var(--accent))] transition-colors"
                                placeholder="e.g. lbbw, ubs, admin"
                                required
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Password</span>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                data-testid="login-password"
                                className="mt-1 w-full px-3 py-2.5 text-sm bg-[hsl(var(--surface-raised))] border border-[hsl(var(--line))] rounded outline-none focus:border-[hsl(var(--accent))] transition-colors"
                                required
                            />
                        </label>
                        {error && (
                            <div data-testid="login-error" className="text-xs text-[hsl(var(--warning))] bg-[hsl(var(--warning-soft))] border border-[hsl(var(--warning))]/30 rounded px-3 py-2">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={submitting || !username || !password}
                            data-testid="login-submit"
                            className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[hsl(var(--accent))] text-white rounded transition-opacity disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <p className="text-[11px] text-[hsl(var(--mute))] leading-relaxed text-center pt-2 border-t border-[hsl(var(--line))]">
                        Credentials are issued per-client by Zühlke. Do not share across organisations.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default Login;
